// deno-lint-ignore-file no-unused-vars
import { h, Fragment } from '../jsx-runtime.ts';
import type { TaskWithTags } from '../types.ts';
import { TasksAPI } from '../api.ts';
import { showToast } from './tmk-toast.tsx';
import { renderTagBadge } from './tmk-tag-badge.tsx';
import { UndoManager } from '../undo.ts';
import type { UpdateTaskInput } from '../types.ts';
import { dateInputToIso, isoToDateInput, deadlineStatus, localDateISO } from '../dates.ts';
import type { ExtrasFocus } from './tmk-task-extras.tsx';
import { parseDuration, formatDuration, DURATION_HINT } from '../duration.ts';
import { recurrenceLabel } from '../recurrence-core.ts';
import { renderRecurrencePicker } from './tmk-recurrence-picker.tsx';

export interface TaskMetaCallbacks {
  onRerender: () => Promise<void>;
  onOpenTagPopup: (e: MouseEvent, tagsArea: HTMLElement, task: TaskWithTags) => void;
  /** Panel załączników w karcie: 'attachments' = otwórz, null = zwiń. */
  onToggleExtras: (task: TaskWithTags, focus: ExtrasFocus) => void;
  isExtrasOpen: (taskId: string) => boolean;
  /** 📝 — edycja opisu W MIEJSCU w wierszu (spec: Ikony 📝 📎 🔁 → Opis w wierszu). */
  onEditDescription: (task: TaskWithTags) => void;
}

/**
 * Escape podczas edycji pola inline = anuluj bez zapisu (spec: Inline editing). Zwraca funkcję
 * sprawdzającą, czy edycję anulowano — handlery change/blur mają wtedy nic nie zapisywać.
 */
function escapeCancels(input: HTMLElement, restore: () => void): () => boolean {
  let cancelled = false;
  input.addEventListener('keydown', (e: Event) => {
    if ((e as KeyboardEvent).key === 'Escape') {
      e.stopPropagation();
      cancelled = true;
      restore();
    }
  });
  return () => cancelled;
}

export function renderTaskMeta(task: TaskWithTags, callbacks: TaskMetaCallbacks): HTMLElement {
  // 📝 jako PIERWSZA ikona — opis leży bezpośrednio nad meta row (spec: Ikony 📝 📎 🔁).
  return <div className="task-meta">
    {renderDescriptionIcon(task, callbacks)}
    {renderDeadline(task, callbacks)}
    {task.deadline ? renderExecTime(task) : null}
    {renderEstimated(task)}
    {renderPlannedStart(task)}
    {task.completed ? renderClosedAt(task, 'completed') : null}
    {task.cancelled ? renderClosedAt(task, 'cancelled') : null}
    {renderAssignedUser(task)}
    {renderHiddenToggle(task)}
    {renderRecurrence(task)}
    {renderAttachmentsIcon(task, callbacks)}
    {renderTags(task, callbacks)}
  </div> as HTMLElement;
}

/** 📝 — opis: edycja w miejscu w wierszu (spec: Ikony 📝 📎 🔁 → Opis w wierszu). */
function renderDescriptionIcon(task: TaskWithTags, cb: TaskMetaCallbacks): HTMLElement {
  const has = !!task.description;
  const span = <span className="task-extras-icon" title={has ? 'Edytuj opis' : 'Dodaj opis'}
    style={{ cursor: 'pointer', opacity: has ? '1' : '0.35' }}>📝</span> as HTMLElement;
  span.addEventListener('click', (e: Event) => { e.stopPropagation(); cb.onEditDescription(task); });
  return span;
}

/** 📎 — załączniki z licznikiem: otwiera panel szczegółów na sekcji załączników. */
function renderAttachmentsIcon(task: TaskWithTags, cb: TaskMetaCallbacks): HTMLElement {
  const n = task.attachments_count ?? 0;
  const span = <span className="task-extras-icon" title={n > 0 ? `Załączniki: ${n} — kliknij, aby zobaczyć` : 'Dodaj załącznik'}
    style={{ cursor: 'pointer', opacity: n > 0 ? '1' : '0.35' }}>{n > 0 ? '📎 ' + n : '📎'}</span> as HTMLElement;
  span.addEventListener('click', (e: Event) => { e.stopPropagation(); cb.onToggleExtras(task, 'attachments'); });
  return span;
}

/**
 * 🔁 — cykliczność: klik → picker (presety + „Co X…” z liczbą i jednostką), zapis natychmiast po zmianie,
 * Escape anuluje, wyjście fokusu poza kontrolki zamyka edytor (spec: Ikony 📝 📎 🔁).
 */
function renderRecurrence(task: TaskWithTags): HTMLElement {
  const label = () => task.recurrence_rule ? '🔁 ' + recurrenceLabel(task.recurrence_rule) : '🔁';
  const span = <span className="task-recurrence" title="Cykliczność — po zamknięciu wystąpienia powstaje następne z nowym deadline"
    style={{ cursor: 'pointer', opacity: task.recurrence_rule ? '1' : '0.35' }}>{label()}</span> as HTMLElement;

  span.addEventListener('click', () => {
    if (span.querySelector('.recurrence-picker')) return;
    let closed = false;
    const close = () => {
      if (closed) return;
      closed = true;
      span.textContent = label();
      span.style.opacity = task.recurrence_rule ? '1' : '0.35';
    };
    const save = async (val: string | null) => {
      if (val === (task.recurrence_rule || null)) return;
      try {
        UndoManager.push({ type: 'update', taskId: task.id, oldValues: { recurrence_rule: task.recurrence_rule } });
        const updated = await TasksAPI.update(task.id, { recurrence_rule: val });
        task.recurrence_rule = updated.recurrence_rule;   // postać kanoniczna z serwera (every:1:week → weekly)
        if (val && !task.deadline) showToast('Cykliczność zapisana. Seria wymaga deadline, żeby powstało następne wystąpienie.', 'info');
      } catch (err: unknown) {
        showToast('Błąd zapisu cykliczności: ' + (err as Error).message, 'error');
      }
    };
    const picker = renderRecurrencePicker({
      value: task.recurrence_rule,
      onChange: async (rule, source) => {
        if (isCancelled()) return;
        await save(rule);
        if (source === 'preset') close();   // wybór z listy zamyka; liczba/jednostka zostają otwarte do dalszych zmian
        else if (!closed) span.style.opacity = '1';
      },
    });
    span.textContent = '';
    span.appendChild(picker.el);
    const isCancelled = escapeCancels(picker.el, close);
    picker.el.addEventListener('focusout', (e: Event) => {
      const next = (e as FocusEvent).relatedTarget as Node | null;
      if (next && picker.el.contains(next)) return;
      // Zapis poszedł już przy change; po chwili sprawdzamy, czy fokus naprawdę wyszedł (Escape zamyka sam).
      setTimeout(() => { if (!closed && !picker.el.contains(document.activeElement)) close(); }, 0);
    });
    picker.focus();
  });

  return span;
}

/** Kolor terminu (spec: meta row → Kolor deadline'u): tylko dla zadań otwartych. */
function applyDeadlineClass(span: HTMLElement, task: TaskWithTags) {
  span.classList.remove('deadline-overdue', 'deadline-today', 'deadline-tomorrow');
  if (task.completed || task.cancelled) { span.title = 'Deadline'; return; }
  const status = deadlineStatus(task.deadline, localDateISO());
  if (status) span.classList.add('deadline-' + status);
  span.title = status === 'overdue' ? 'Deadline — po terminie'
    : status === 'today' ? 'Deadline — mija dziś'
    : status === 'tomorrow' ? 'Deadline — mija jutro'
    : 'Deadline';
}

function renderDeadline(task: TaskWithTags, cb: TaskMetaCallbacks): HTMLElement {
  const span = <span className="task-deadline" title="Deadline" style={{ cursor: 'pointer' }}>
    {'📅 ' + (task.deadline ?? '—')}
  </span> as HTMLElement;
  applyDeadlineClass(span, task);

  span.addEventListener('click', () => {
    if (span.querySelector('input')) return;
    const input = <input type="date" className="inline-edit-input" value={task.deadline ?? ''} /> as HTMLInputElement;
    span.textContent = '';
    span.appendChild(input);
    input.focus();
    const isCancelled = escapeCancels(input, () => { span.textContent = '📅 ' + (task.deadline ?? '—'); });

    input.addEventListener('change', async () => {
      if (isCancelled()) return;
      const val = input.value || null;
      try {
        const data: Record<string, unknown> = { deadline: val };
        if (!val) data.execution_time = null;
        UndoManager.push({ type: 'update', taskId: task.id, oldValues: { deadline: task.deadline, execution_time: task.execution_time } });
        await TasksAPI.update(task.id, data);
        task.deadline = val;
        if (!val) task.execution_time = null;
        await cb.onRerender();
      } catch (err: unknown) {
        showToast('Błąd zapisu deadline: ' + (err as Error).message, 'error');
      }
    });

    input.addEventListener('blur', () => {
      if (span.querySelector('input')) {
        span.textContent = '📅 ' + (task.deadline ?? '—');
        applyDeadlineClass(span, task);
      }
    });
  });

  return span;
}

function renderExecTime(task: TaskWithTags): HTMLElement {
  const span = <span className="task-exec-time" title="Czas wykonania" style={{ cursor: 'pointer' }}>
    {'⏰ ' + (task.execution_time ?? '—')}
  </span> as HTMLElement;

  span.addEventListener('click', () => {
    if (span.querySelector('input')) return;
    const input = <input type="time" className="inline-edit-input" value={task.execution_time ?? ''} /> as HTMLInputElement;
    span.textContent = '';
    span.appendChild(input);
    input.focus();
    const isCancelled = escapeCancels(input, () => { span.textContent = '⏰ ' + (task.execution_time ?? '—'); });

    const save = async () => {
      if (isCancelled() || !span.querySelector('input')) return;
      const val = input.value || null;
      try {
        UndoManager.push({ type: 'update', taskId: task.id, oldValues: { execution_time: task.execution_time } });
        await TasksAPI.update(task.id, { execution_time: val });
        task.execution_time = val;
      } catch (err: unknown) {
        showToast('Błąd zapisu godziny: ' + (err as Error).message, 'error');
      }
      span.textContent = '⏰ ' + (task.execution_time ?? '—');
    };

    input.addEventListener('change', save);
    input.addEventListener('blur', save);
  });

  return span;
}

function renderEstimated(task: TaskWithTags): HTMLElement {
  // Czasochłonność w minutach / godzinach / dniach (spec: Inline editing → Czasochłonność); parser w duration.ts.
  const label = () => task.estimated_minutes != null ? '⏳ ' + formatDuration(task.estimated_minutes) : '⏳ —';
  const span = <span className="task-estimated" title={'Szacowana czasochłonność — ' + DURATION_HINT} style={{ cursor: 'pointer' }}>
    {label()}
  </span> as HTMLElement;

  span.addEventListener('click', () => {
    if (span.querySelector('input')) return;
    const input = <input type="text" className="inline-edit-input" style={{ width: '90px' }}
      value={task.estimated_minutes != null ? formatDuration(task.estimated_minutes) : ''} placeholder="np. 1.5h"
      title={DURATION_HINT} /> as HTMLInputElement;
    span.textContent = '';
    span.appendChild(input);
    input.focus();
    input.select();
    const restoreEst = () => { span.textContent = label(); };
    const isCancelled = escapeCancels(input, restoreEst);

    const save = async () => {
      if (isCancelled() || !span.querySelector('input')) return;
      const val = parseDuration(input.value);
      if (val === undefined) {
        showToast('Nie rozumiem czasu „' + input.value.trim() + '” — ' + DURATION_HINT, 'error');
      } else if (val !== task.estimated_minutes) {
        try {
          UndoManager.push({ type: 'update', taskId: task.id, oldValues: { estimated_minutes: task.estimated_minutes } });
          await TasksAPI.update(task.id, { estimated_minutes: val });
          task.estimated_minutes = val;
        } catch (err: unknown) {
          showToast('Błąd zapisu czasochłonności: ' + (err as Error).message, 'error');
        }
      }
      restoreEst();
    };

    input.addEventListener('blur', save);
    input.addEventListener('keydown', (e: Event) => {
      if ((e as KeyboardEvent).key === 'Enter') { e.preventDefault(); input.blur(); }
    });
  });

  return span;
}

function renderPlannedStart(task: TaskWithTags): HTMLElement {
  const label = task.planned_start_date
    ? '▷ ' + task.planned_start_date + (task.planned_start_time ? ' ' + task.planned_start_time : '')
    : '▷ —';
  const span = <span className="task-planned-start" title="Planowany start" style={{ cursor: 'pointer' }}>
    {label}
  </span> as HTMLElement;

  span.addEventListener('click', () => {
    if (span.querySelector('input')) return;
    const wrapper = <span style={{ display: 'inline-flex', gap: '4px' }} /> as HTMLElement;

    const dateInput = <input type="date" className="inline-edit-input"
      value={task.planned_start_date ?? ''} /> as HTMLInputElement;
    wrapper.appendChild(dateInput);

    let timeInput: HTMLInputElement | null = null;
    if (task.planned_start_date) {
      timeInput = <input type="time" className="inline-edit-input"
        value={task.planned_start_time ?? ''} /> as HTMLInputElement;
      wrapper.appendChild(timeInput);
    }

    span.textContent = '';
    span.appendChild(wrapper);
    dateInput.focus();
    const isCancelledDate = escapeCancels(dateInput, () => refreshLabel());
    const isCancelledTime = escapeCancels(wrapper, () => refreshLabel());
    const isCancelled = () => isCancelledDate() || isCancelledTime();

    const save = async () => {
      if (isCancelled() || !span.querySelector('input')) return;
      const dateVal = dateInput.value || null;
      const timeVal = timeInput ? (timeInput.value || null) : null;
      try {
        UndoManager.push({ type: 'update', taskId: task.id, oldValues: { planned_start_date: task.planned_start_date, planned_start_time: task.planned_start_time } });
        await TasksAPI.update(task.id, {
          planned_start_date: dateVal,
          planned_start_time: dateVal ? timeVal : null,
        });
        task.planned_start_date = dateVal;
        task.planned_start_time = dateVal ? timeVal : null;
      } catch (err: unknown) {
        showToast('Błąd zapisu planowanego startu: ' + (err as Error).message, 'error');
      }
      refreshLabel();
    };

    const refreshLabel = () => {
      span.textContent = task.planned_start_date
        ? '▷ ' + task.planned_start_date + (task.planned_start_time ? ' ' + task.planned_start_time : '')
        : '▷ —';
    };

    dateInput.addEventListener('change', async () => {
      if (isCancelled()) return;
      if (dateInput.value && !timeInput) {
        timeInput = <input type="time" className="inline-edit-input" value="" /> as HTMLInputElement;
        wrapper.appendChild(timeInput);
        timeInput.addEventListener('change', save);
        timeInput.addEventListener('blur', save);
      } else if (!dateInput.value && timeInput) {
        timeInput.remove();
        timeInput = null;
      }
      await save();
    });

    if (timeInput) {
      timeInput.addEventListener('change', save);
      timeInput.addEventListener('blur', save);
    }

    dateInput.addEventListener('blur', () => {
      setTimeout(() => {
        if (!wrapper.contains(document.activeElement)) refreshLabel();
      }, 150);
    });
  });

  return span;
}

/** ✓ data ukończenia / ⊘ data anulowania — klik → date picker (spec/lista-zadan.md, "Data ukończenia / anulowania"). */
function renderClosedAt(task: TaskWithTags, kind: 'completed' | 'cancelled'): HTMLElement {
  const field = kind === 'completed' ? 'completed_at' : 'cancelled_at';
  const icon = kind === 'completed' ? '✓' : '⊘';
  const label = () => icon + ' ' + (task[field] ? new Date(task[field]!).toLocaleDateString('pl') : '—');
  const span = <span className={'task-' + kind + '-at'}
    title={(kind === 'completed' ? 'Data ukończenia' : 'Data anulowania') + ' — kliknij, aby zmienić'}
    style={{ cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}
  >{label()}</span> as HTMLElement;

  span.addEventListener('click', () => {
    if (span.querySelector('input')) return;
    const input = <input type="date" className="inline-edit-input" value={isoToDateInput(task[field])} /> as HTMLInputElement;
    span.textContent = '';
    span.appendChild(input);
    input.focus();
    const isCancelled = escapeCancels(input, () => { span.textContent = label(); });

    input.addEventListener('change', async () => {
      if (isCancelled()) return;
      if (!input.value) { span.textContent = label(); return; }
      const iso = dateInputToIso(input.value, task[field]);
      const data: UpdateTaskInput = kind === 'completed' ? { completed_at: iso } : { cancelled_at: iso };
      try {
        UndoManager.push({ type: 'update', taskId: task.id, oldValues: { [field]: task[field] } });
        await TasksAPI.update(task.id, data);
        task[field] = iso;
      } catch (err: unknown) {
        showToast('Błąd zapisu daty: ' + (err as Error).message, 'error');
      }
      span.textContent = label();
    });

    input.addEventListener('blur', () => {
      if (span.querySelector('input')) span.textContent = label();
    });
  });

  return span;
}

function renderAssignedUser(task: TaskWithTags): HTMLElement {
  const shortEmail = (email: string) => email.split('@')[0];
  const label = task.assigned_email ? '👤 ' + shortEmail(task.assigned_email) : '👤 —';
  const span = <span className="task-assigned" title={task.assigned_email ? 'Przypisano: ' + task.assigned_email : 'Przypisz osobę'} style={{ cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}>
    {label}
  </span> as HTMLElement;

  span.addEventListener('click', () => {
    if (span.querySelector('input')) return;
    const input = <input type="email" className="inline-edit-input"
      style={{ width: '140px' }} value={task.assigned_email ?? ''} placeholder="email"
    /> as HTMLInputElement;
    span.textContent = '';
    span.appendChild(input);
    input.focus();
    input.select();

    const save = async () => {
      const val = input.value.trim() || null;
      if (val !== task.assigned_email) {
        try {
          UndoManager.push({ type: 'update', taskId: task.id, oldValues: { assigned_email: task.assigned_email } });
          await TasksAPI.update(task.id, { assigned_email: val });
          task.assigned_email = val;
        } catch (err: unknown) {
          showToast('Błąd przypisania: ' + (err as Error).message, 'error');
        }
      }
      span.textContent = task.assigned_email ? '👤 ' + shortEmail(task.assigned_email) : '👤 —';
      span.title = task.assigned_email ? 'Przypisano: ' + task.assigned_email : 'Przypisz osobę';
    };

    input.addEventListener('blur', save);
    input.addEventListener('keydown', (e: Event) => {
      if ((e as KeyboardEvent).key === 'Enter') { e.preventDefault(); input.blur(); }
      if ((e as KeyboardEvent).key === 'Escape') { input.value = task.assigned_email ?? ''; input.blur(); }
    });
  });

  return span;
}

function renderHiddenToggle(task: TaskWithTags): HTMLElement {
  const span = <span
    className="task-hidden-toggle"
    title={task.hidden ? 'Ukryte — widoczne tylko dla Ciebie' : 'Widoczne dla wszystkich'}
    style={{ cursor: 'pointer', opacity: task.hidden ? '1' : '0.35' }}
  >{task.hidden ? '🔒' : '👁'}</span> as HTMLElement;

  span.addEventListener('click', async () => {
    try {
      UndoManager.push({ type: 'update', taskId: task.id, oldValues: { hidden: task.hidden } });
      await TasksAPI.update(task.id, { hidden: !task.hidden });
      task.hidden = !task.hidden;
      span.textContent = task.hidden ? '🔒' : '👁';
      span.title = task.hidden ? 'Ukryte — widoczne tylko dla Ciebie' : 'Widoczne dla wszystkich';
      span.style.opacity = task.hidden ? '1' : '0.35';
    } catch (err: unknown) {
      showToast('Błąd zmiany widoczności: ' + (err as Error).message, 'error');
    }
  });

  return span;
}

function renderTags(task: TaskWithTags, cb: TaskMetaCallbacks): HTMLElement {
  const tagsArea = <span className="task-tags-area" style={{ cursor: 'pointer', position: 'relative' }} /> as HTMLElement;

  if (task.tags && task.tags.length > 0) {
    for (const tag of task.tags) {
      tagsArea.appendChild(renderTagBadge(tag));
    }
  } else {
    const placeholder = <span className="tag-badge" style={{ opacity: '0.5' }}>+ tag</span>;
    tagsArea.appendChild(placeholder);
  }

  tagsArea.addEventListener('click', (e: Event) => cb.onOpenTagPopup(e as MouseEvent, tagsArea, task));

  return tagsArea;
}
